/**
 * This file is based on https://github.com/closeio/mic-recorder-to-mp3/blob/master/src/mic-recorder.js
 *
 * @license MIT Copyright (c) 2017 Elastic Inc. (Close.io) see ./LICENSE
 */

import Encoder from './Encoder'

interface MicRecorderConfig {
  bitRate?: number
  startRecordingAt?: number
  deviceId?: string | null
  sampleRate?: number
  /**
   * Request unprocessed audio (noiseSuppression / echoCancellation /
   * autoGainControl all off). Advisory — read back via getAppliedSettings().
   */
  originalAudio?: boolean
}

/**
 * This class does not use MediaRecorder API since at
 * the time of writing Chrome only supported the audio
 * codec weba (Web Audio) which is not supported by iOS
 */
class MicRecorder {
  public audioSignalDetected: boolean = false
  // Set true when start() had to drop a stale persisted deviceId and fall back
  // to the system default; the caller resets the stored setting on seeing it.
  public deviceIdWasStale: boolean = false
  // Gate checked by onaudioprocess: while paused, frames are neither encoded
  // nor volume-metered, so the resulting MP3 is one contiguous stream (same
  // encoder, no re-init) and the waveform/meter freeze.
  // ponytail: the mic stream stays live while paused, so the OS/tab "recording"
  // indicator stays on. Upgrade path if that matters: toggle track.enabled.
  public paused: boolean = false
  // Frame gate while a device hot-swap is mid-flight. Separate from `paused`
  // (the user's pause is authoritative and untouched during a swap) so the two
  // can't clobber each other; also doubles as the reentrancy guard.
  private swapping: boolean = false
  private onVolumeChange: (volume: number) => void
  private config: MicRecorderConfig
  private activeStream: MediaStream | null
  private context: AudioContext | null
  private microphone: MediaStreamAudioSourceNode | null
  private processor: ScriptProcessorNode | null
  private timerToStart?: number
  private lameEncoder: Encoder | null

  constructor(
    onVolumeChange: (volume: number) => void,
    config: MicRecorderConfig = {}
  ) {
    this.config = {
      bitRate: 128,
      startRecordingAt: 300,
      deviceId: null,
      ...config,
    }
    this.onVolumeChange = onVolumeChange
    this.activeStream = null
    this.context = null
    this.microphone = null
    this.processor = null
    this.lameEncoder = null
  }

  /**
   * Starts to listen for the microphone sound
   * @param {MediaStream} stream
   */
  private addMicrophoneListener(stream: MediaStream): void {
    this.activeStream = stream

    // This prevents the weird noise once you start listening to the microphone
    this.timerToStart = window.setTimeout(() => {
      delete this.timerToStart
    }, this.config.startRecordingAt)

    // Set up Web Audio API to process data from the media stream (microphone).
    if (!this.context) {
      throw new Error('AudioContext is not initialized.')
    }

    this.microphone = this.context.createMediaStreamSource(stream)

    // Settings a bufferSize of 0 instructs the browser to choose the best bufferSize
    this.processor = this.context.createScriptProcessor(0, 1, 1)

    // Add all buffers from LAME into an array.
    this.processor.onaudioprocess = (event: AudioProcessingEvent) => {
      // The contiguous-MP3 invariant (paused frames are skipped, resume feeds
      // the SAME encoder → one stream) is exercised by the screenshot/e2e
      // harness, not the unit suite — it needs a live WebAudio pipeline.
      if (this.timerToStart || this.paused || this.swapping) {
        return
      }

      if (this.lameEncoder) {
        // Send microphone data to LAME for MP3 encoding while recording.
        this.lameEncoder.encode(event.inputBuffer.getChannelData(0))
      }
      this.calculateVolume(event.inputBuffer.getChannelData(0))
    }

    // Begin retrieving microphone data.
    this.microphone.connect(this.processor)
    this.processor.connect(this.context.destination)
  }

  /**
   * Disconnect microphone, processor and remove activeStream
   */
  stop(): this {
    if (this.processor && this.microphone) {
      // Clean up the Web Audio API resources.
      this.microphone.disconnect()
      this.processor.disconnect()

      if (this.context && this.context.state !== 'closed') {
        this.context.close()
      }

      this.processor.onaudioprocess = null

      if (this.activeStream) {
        // Stop all audio tracks. Also, removes recording icon from chrome tab
        this.activeStream.getAudioTracks().forEach(track => track.stop())
      }
    }

    return this
  }

  /**
   * getUserMedia audio constraints for a given deviceId. `deviceId: { exact }`
   * pins the device; originalAudio flips the advisory processing flags off.
   * Returns `true` (default device, no processing constraints) when empty.
   */
  private audioConstraints(
    deviceId: string | null
  ): MediaTrackConstraints | true {
    const constraints: MediaTrackConstraints = {}
    if (deviceId) {
      constraints.deviceId = { exact: deviceId }
    }
    if (this.config.originalAudio) {
      // Advisory: browsers/mics may ignore these. Caller reads back the truth
      // via getAppliedSettings() and reflects it in the UI.
      constraints.noiseSuppression = false
      constraints.echoCancellation = false
      constraints.autoGainControl = false
    }
    return Object.keys(constraints).length > 0 ? constraints : true
  }

  /**
   * Requests access to the microphone and start recording.
   * @return Promise<MediaStream>
   */
  start(): Promise<MediaStream> {
    const AudioContext =
      window.AudioContext || (window as any).webkitAudioContext
    this.context = new AudioContext()
    this.config.sampleRate = this.context.sampleRate
    this.lameEncoder = new Encoder(this.config)

    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({
          audio: this.audioConstraints(this.config.deviceId ?? null),
        })
        .catch((err: any) => {
          // Stale/unplugged persisted deviceId → OverconstrainedError (or
          // NotFoundError). Retry on the system default instead of failing the
          // whole recording; the caller resets the stored setting.
          if (
            this.config.deviceId &&
            (err?.name === 'OverconstrainedError' ||
              err?.name === 'NotFoundError')
          ) {
            this.deviceIdWasStale = true
            this.config.deviceId = null
            return navigator.mediaDevices.getUserMedia({
              audio: this.audioConstraints(null),
            })
          }
          throw err
        })
        .then(stream => {
          this.addMicrophoneListener(stream)
          resolve(stream)
        })
        .catch(err => {
          // getUserMedia (incl. the stale-device fallback) failed: close the
          // AudioContext start() opened up-front. The caller's error path does
          // not call stop(), and stop() wouldn't close it anyway (its cleanup
          // is gated on a microphone/processor that never got wired), so
          // repeated denials would otherwise leak contexts until the browser's
          // concurrent-AudioContext cap throws.
          if (this.context && this.context.state !== 'closed') {
            this.context.close()
          }
          this.context = null
          reject(err)
        })
    })
  }

  /**
   * Hot-swap the input device mid-recording without breaking the take: open the
   * new stream, then swap the source node feeding the SAME processor/encoder so
   * the MP3 stays one contiguous stream. Frames are gated via the internal
   * `swapping` flag during the swap so no garbage lands — the user's pause flag
   * stays authoritative and untouched. Opening the new stream before tearing
   * down the old one means a failed switch (e.g. the device vanished) keeps the
   * current take running on the old device.
   *
   * Alive-check is on context.state, not context/processor being non-null:
   * stop() closes the context but leaves the fields set, so a stopped recorder
   * still has a non-null context.
   */
  async switchDevice(deviceId: string | null): Promise<void> {
    const processor = this.processor
    if (!this.context || this.context.state === 'closed' || processor == null) {
      // Not recording (or already stopped) — next start() picks up the new
      // deviceId via config.
      this.config.deviceId = deviceId
      return
    }
    if (this.swapping) {
      // ponytail: a swap is already in flight — drop this call. The menu closes
      // on select so a queued switch isn't worth building; upgrade path is a
      // pending-deviceId re-run in finally if callers ever spam it.
      return
    }
    this.swapping = true // gate frames + block a reentrant swap during the await
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: this.audioConstraints(deviceId),
      })
      const ctx = this.context
      if (ctx == null || ctx.state === 'closed') {
        // Recorder was stopped while getUserMedia resolved — don't leak the
        // freshly opened stream, and don't wire a node onto a dead context.
        newStream.getAudioTracks().forEach(track => track.stop())
        return
      }
      try {
        this.microphone?.disconnect()
        this.activeStream?.getAudioTracks().forEach(track => track.stop())
        this.config.deviceId = deviceId
        this.activeStream = newStream
        this.microphone = ctx.createMediaStreamSource(newStream)
        this.microphone.connect(processor)
      } catch (err) {
        // Any wiring failure: stop the new stream so it can't leak, then rethrow.
        newStream.getAudioTracks().forEach(track => track.stop())
        throw err
      }
    } finally {
      this.swapping = false
    }
  }

  /**
   * Return Mp3 Buffer and Blob with type mp3
   * @return {Promise<[Int8Array[], Blob]>}
   */
  getMp3(): Promise<[Int8Array[], Blob]> {
    if (!this.lameEncoder) {
      return Promise.reject(new Error('LAME encoder is not initialized.'))
    }

    const finalBuffer = this.lameEncoder.finish()

    return new Promise((resolve, reject) => {
      if (finalBuffer.length === 0) {
        reject(new Error('No buffer to send'))
      } else {
        resolve([
          finalBuffer,
          new Blob(
            finalBuffer.map(buffer => new Uint8Array(buffer)),
            { type: 'audio/mp3' }
          ),
        ])
        this.lameEncoder?.clearBuffer()
      }
    })
  }

  /** Stop feeding frames to the encoder (mic stays live). */
  pause(): void {
    this.paused = true
  }

  /** Resume feeding frames into the SAME encoder → one contiguous MP3. */
  resume(): void {
    this.paused = false
  }

  /**
   * Actual audio track settings after the stream opened, or null if not
   * recording. Used to confirm advisory constraints (e.g. originalAudio).
   */
  getAppliedSettings(): MediaTrackSettings | null {
    const track = this.activeStream?.getAudioTracks()[0]
    return track ? track.getSettings() : null
  }

  /**
   * Return the volume of the microphone
   * @return {number}
   */
  calculateVolume(input: any) {
    let sum = 0.0
    for (let i = 0; i < input.length; ++i) {
      sum += input[i] * input[i]
    }
    if (sum > 0) {
      this.audioSignalDetected = true
    }
    this.onVolumeChange(Math.sqrt(sum / input.length))
  }
}

export default MicRecorder
